import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useActiveOrganization } from "../context/AuthContext";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/Card";
import { OpsEmptyState, SectionHeader, InventorySkeleton } from "../components/ops-display";
import { InspectPanel } from "../components/InspectPanel";
import {
  VersionInspectSummary,
  VersionsFilterBar,
  VersionsInventoryList,
} from "../components/versions-inventory";
import { api, ApiError } from "../lib/api";
import {
  VERSIONS_PAGE_LABEL,
  VERSIONS_PAGE_NOTE,
  VERSIONS_RC_DISTINCTION_NOTE,
  filterAndSortVersions,
  type VersionSnapshotRow,
  type VersionSortKey,
} from "../lib/versions-ops";

const PAGE_LIMIT = 100;

export function VersionsPage() {
  const activeOrg = useActiveOrganization();
  const organizationLabel =
    activeOrg.code || activeOrg.displayName || activeOrg.organizationId || "—";

  const [items, setItems] = useState<VersionSnapshotRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [listFilter, setListFilter] = useState("all");
  const [sort, setSort] = useState<VersionSortKey>("time");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [rollbackTarget, setRollbackTarget] = useState<VersionSnapshotRow | null>(null);
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.listSnapshots({ limit: PAGE_LIMIT, offset });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "بارگذاری ناموفق");
    } finally {
      setLoading(false);
    }
  }, [offset]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (selectedId && !items.some((r) => r.id === selectedId)) {
      setSelectedId(null);
    }
  }, [items, selectedId]);

  const filtered = useMemo(
    () => filterAndSortVersions(items, { query, filter: listFilter, sort }),
    [items, query, listFilter, sort],
  );

  const selected = useMemo(
    () => items.find((r) => r.id === selectedId) ?? null,
    [items, selectedId],
  );

  async function handleRollback(e: FormEvent) {
    e.preventDefault();
    if (!rollbackTarget || rollbackTarget.kind !== "promotion") return;
    setPending(true);
    setError(null);
    try {
      await api.rollbackPromotion(rollbackTarget.contentItemId, rollbackTarget.id, password);
      setSuccess(`بازگردانی به نسخه ${rollbackTarget.version} انجام شد.`);
      setRollbackTarget(null);
      setPassword("");
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "بازگردانی ناموفق");
    } finally {
      setPending(false);
    }
  }

  function renderVersionActions(row: VersionSnapshotRow): ReactNode {
    if (row.kind === "promotion") {
      return (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => {
            setRollbackTarget(row);
            setPassword("");
          }}
        >
          بازگردانی
        </button>
      );
    }
    if (row.kind === "announcement") {
      return (
        <Link to="/announcements" className="btn btn--ghost btn--sm">
          اطلاعیه
        </Link>
      );
    }
    return null;
  }

  return (
    <>
      <PageHeader title={VERSIONS_PAGE_LABEL} />

      <aside
        className="ops-safety-summary ops-safety-summary--warn"
        role="note"
        data-vt-versions-safety
      >
        <p className="ops-safety-summary__eyebrow">تمایز تاریخچه</p>
        <ul className="ops-safety-summary__points">
          <li>تاریخچهٔ محتوای CMS — نه Bundle پورتال و نه RC منجمد</li>
          <li>بازگردانی محتوا نسخهٔ جدید می‌سازد؛ ZIP/RouterOS را تغییر نمی‌دهد</li>
        </ul>
        <details className="ops-safety-disclosure">
          <summary>توضیح کامل تمایز Bundle / RC</summary>
          <p>{VERSIONS_PAGE_NOTE}</p>
          <p>{VERSIONS_RC_DISTINCTION_NOTE}</p>
        </details>
      </aside>

      {error && <p className="banner banner--error">{error}</p>}
      {success && <p className="banner banner--success">{success}</p>}

      <div className="page-stack page-stack--inventory">
        <div
          className={`content-ops-layout${selected ? " content-ops-layout--inspect-open" : ""}`}
          data-vt-content-ops
          data-vt-inspect-open={selected ? "true" : "false"}
        >
          <Card className="content-ops-layout__list inventory-surface" bodyClassName="card__body--flush">
            <SectionHeader title="فهرست نسخه‌های انتشار" />
            <VersionsFilterBar
              filter={listFilter}
              onFilterChange={setListFilter}
              query={query}
              onQueryChange={setQuery}
              sort={sort}
              onSortChange={setSort}
              organizationLabel={organizationLabel}
              total={total}
              limit={PAGE_LIMIT}
              offset={offset}
            />
            {loading ? (
              <InventorySkeleton />
            ) : items.length === 0 ? (
              <OpsEmptyState title="نسخه‌ای ثبت نشده است" />
            ) : filtered.length === 0 ? (
              <OpsEmptyState title="موردی در این فیلتر نیست" />
            ) : (
              <VersionsInventoryList
                rows={filtered}
                selectedId={selectedId}
                onSelect={setSelectedId}
                renderActions={renderVersionActions}
              />
            )}
            <div className="form-actions" style={{ marginBlockStart: "1rem" }}>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={loading || offset <= 0}
                onClick={() => setOffset((o) => Math.max(0, o - PAGE_LIMIT))}
              >
                صفحه قبل
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={loading || offset + PAGE_LIMIT >= total}
                onClick={() => setOffset((o) => o + PAGE_LIMIT)}
              >
                صفحه بعد
              </button>
            </div>
          </Card>

          {selected && (
            <InspectPanel title="جزئیات نسخه" className="content-inspect-panel">
              <VersionInspectSummary
                item={selected}
                organizationLabel={organizationLabel}
                actions={renderVersionActions(selected)}
              />
            </InspectPanel>
          )}
        </div>
      </div>

      {rollbackTarget && (
        <div className="modal-backdrop" role="presentation" onClick={() => setRollbackTarget(null)}>
          <div className="modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <h2>تأیید بازگردانی</h2>
            <p>
              بازگردانی «{rollbackTarget.title}» به نسخه{" "}
              <bdi dir="ltr" className="ltr-isolate">
                {rollbackTarget.version}
              </bdi>
            </p>
            <form onSubmit={handleRollback}>
              <label>
                <span>رمز عبور برای تأیید</span>
                <input
                  type="password"
                  dir="ltr"
                  className="ltr-isolate"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </label>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setRollbackTarget(null)}
                >
                  انصراف
                </button>
                <button type="submit" className="btn btn--danger" disabled={pending} aria-busy={pending}>
                  {pending ? "در حال پردازش…" : "تأیید بازگردانی"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
